import type { ReactNode } from "react"
import { cn } from "~/utils/helpers"

/**
 * Matrix/pivot table with both row AND column headers.
 * Use for: Cross-tabulated data, 2D matrices (e.g., prices by warehouse×channel, comparisons by attribute×product)
 * For simple column-based tables without row headers, use MiniTable instead.
 *
 * @deprecated Use the design-system `Matrix` (`~/components/design-system/components`) for new UI.
 */
interface MiniMatrixProps {
  columnHeaders: string[]
  rowHeaders: string[]
  data: Array<Array<string | number | ReactNode | undefined>>
  className?: string
  emptyText?: string
  noBorder?: boolean
  centerText?: boolean
  onCellHover?: (rowIndex: number, colIndex: number) => void
  onCellLeave?: () => void
  hoveredCell?: { rowIndex: number; colIndex: number } | null
}

export function MiniMatrix({
  columnHeaders,
  rowHeaders,
  data,
  className,
  emptyText = "No data",
  noBorder = false,
  centerText = false,
  onCellHover,
  onCellLeave,
  hoveredCell,
}: MiniMatrixProps) {
  const numColumns = columnHeaders.length
  const numRows = rowHeaders.length

  const renderCell = (content: string | number | ReactNode | undefined) => {
    if (content === undefined || content === null) {
      return <span className="text-fg-muted">{emptyText}</span>
    }
    return content
  }

  return (
    <div
      className={cn(
        "w-full max-h-[400px] overflow-y-auto bg-border-base grid",
        !noBorder && "rounded-lg border border-border-base",
        className,
      )}
      style={{
        gridTemplateColumns: `auto repeat(${numColumns}, minmax(0, 1fr))`,
        gap: "1px",
      }}
    >
      {/* Top-left corner cell (empty) */}
      <div className="bg-subtle px-2 py-[6px] paragraph-base font-medium text-fg-subtle rounded-tl-lg" />

      {/* Column Headers */}
      {columnHeaders.map((header, colIndex) => (
        <div
          key={`col-header-${colIndex}`}
          className={cn(
            "bg-subtle px-2 py-[6px] paragraph-base font-medium text-fg-subtle truncate",
            centerText && "text-center",
            colIndex === numColumns - 1 && "rounded-tr-lg",
          )}
        >
          {header}
        </div>
      ))}

      {/* Data Rows with Row Headers - flattened as direct children */}
      {rowHeaders.map((rowHeader, rowIndex) => (
        <>
          {/* Row Header */}
          <div
            key={`row-header-${rowIndex}`}
            className={cn(
              "bg-subtle px-2 py-[6px] paragraph-sm font-medium text-fg-subtle truncate",
              rowIndex === numRows - 1 && "rounded-bl-lg",
            )}
          >
            {rowHeader}
          </div>

          {/* Data Cells */}
          {Array.from({ length: numColumns }).map((_, colIndex) => {
            const cellData = data[rowIndex]?.[colIndex]
            const isHovered =
              hoveredCell?.rowIndex === rowIndex && hoveredCell?.colIndex === colIndex
            return (
              <div
                key={`cell-${rowIndex}-${colIndex}`}
                className={cn(
                  "bg-base px-2 py-[6px] paragraph-sm text-fg-base truncate transition-all",
                  centerText && "text-center",
                  rowIndex === numRows - 1 && colIndex === numColumns - 1 && "rounded-br-lg",
                  onCellHover && "cursor-pointer",
                  isHovered && "ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20",
                )}
                onMouseEnter={() => onCellHover?.(rowIndex, colIndex)}
                onMouseLeave={() => onCellLeave?.()}
              >
                {renderCell(cellData)}
              </div>
            )
          })}
        </>
      ))}
    </div>
  )
}
